import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar, Clock, Plus, Settings, BarChart, Shield, X, Wand2, AlertTriangle
} from 'lucide-react';
import { calcWeekLabourHours, calcShiftHours } from '@/utils/labourHours';

export function ManagerShiftsTab({ ctx }) {
  const {
    weeklyReport, selectedWeek, managerOnDuty, shifts, selectedRoles,
    setSelectedRoles, isOwnBranch, setShowCreateShift, setShowCreateDirectShift,
    handleExportShiftsPDF, getWeekDates, openAssignDialog, handleEditShift,
    handleUnassignUser, branches, selectedBranch, unavailability,
    setSelectedBranch, changeWeek, user,
  } = ctx;

  // Build unavailability lookup
  const unavailMap = React.useMemo(() => {
    const map = {};
    for (const u of (unavailability || [])) {
      const uid = u.user_id;
      if (!map[uid]) map[uid] = {};
      let d = new Date(u.date_from);
      const end = new Date(u.date_to);
      while (d <= end) {
        map[uid][d.toISOString().split('T')[0]] = u.reason + (u.hours ? ` (${u.hours}h)` : '');
        d.setDate(d.getDate() + 1);
      }
    }
    return map;
  }, [unavailability]);

  return (
    <div className="bg-white rounded-xl shadow-md border border-stone-200 p-4">
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="manager-week-nav">
        <Button onClick={() => changeWeek(-1)} variant="outline" size="sm" data-testid="prev-week-button">← Prev</Button>
        <Button onClick={() => changeWeek(1)} variant="outline" size="sm" data-testid="next-week-button">Next →</Button>
        <Select value={selectedBranch} onValueChange={setSelectedBranch}>
          <SelectTrigger className="w-40 h-9" data-testid="branch-selector"><SelectValue /></SelectTrigger>
          <SelectContent>
            {branches.map(b => (
              <SelectItem key={b.id} value={b.name}>{b.name} {b.name === user?.branch ? '(yours)' : ''}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!isOwnBranch && (
          <span className="text-xs font-medium px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid="view-only-banner">
            Viewing {selectedBranch} — Read Only
          </span>
        )}
      </div>
      <div className="flex flex-wrap justify-between items-center mb-2 gap-2">
        <div>
          <h2 className="text-lg font-bold text-stone-800">Weekly Schedule</h2>
          <p className="text-xs text-stone-500">
            Week {weeklyReport?.week_number || ''}, {weeklyReport?.year || new Date(selectedWeek).getFullYear()} | {new Date(selectedWeek).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(new Date(selectedWeek).setDate(new Date(selectedWeek).getDate() + 6)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </p>
          {(() => {
            const { available, allocated } = calcWeekLabourHours(shifts);
            const pct = available > 0 ? Math.round((allocated / available) * 100) : 0;
            const branch = branches?.find(b => b.name === selectedBranch);
            const costRate = branch?.avg_labour_cost_per_hour || 0;
            const allocatedCost = costRate > 0 ? (allocated * costRate) : 0;
            const availableCost = costRate > 0 ? (available * costRate) : 0;
            return (
              <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid="week-labour-hours">
                <span className={`text-sm font-bold ${pct >= 80 ? 'text-green-700' : pct >= 50 ? 'text-orange-600' : 'text-stone-700'}`}>
                  {allocated}h / {available}h
                </span>
                <span className="text-[10px] text-stone-500">labour (excl. lunch)</span>
                <div className="w-24 h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-orange-400' : 'bg-stone-400'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <span className="text-[10px] font-medium text-stone-600">{pct}%</span>
                {costRate > 0 && (
                  <span className="text-xs font-semibold text-stone-700 ml-2 px-2 py-0.5 bg-stone-100 rounded" data-testid="week-labour-cost">
                    R{allocatedCost.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} / R{availableCost.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} cost
                  </span>
                )}
              </div>
            );
          })()}
          {managerOnDuty && (
            <div className="text-xs text-stone-600 flex items-center gap-1 mt-0.5">
              <Shield className="w-3 h-3" />
              MOD: <span className="font-semibold">{managerOnDuty.manager_username}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={handleExportShiftsPDF}
            data-testid="export-shifts-pdf-button"
            className="bg-red-600 hover:bg-red-700 text-white"
            size="sm"
          >
            <BarChart className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          {isOwnBranch && (
            <>
              <Button
                onClick={() => ctx.setShowApplyTemplateWeek(true)}
                data-testid="apply-template-week-button"
                variant="outline"
                className="border-gray-600 text-gray-600 hover:bg-gray-50"
                size="sm"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Apply Template
              </Button>
              <Button
                onClick={() => setShowCreateDirectShift(true)}
                data-testid="create-direct-shift-button"
                className="bg-gradient-to-r from-gray-600 to-gray-700 text-white"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Shift
              </Button>
            </>
          )}
          <Select
            value={selectedRoles.length > 0 ? 'filtered' : 'all'}
            onValueChange={() => {}}
          >
            <SelectTrigger className="w-44" data-testid="role-filter-trigger">
              <SelectValue>
                {selectedRoles.length === 0 ? 'All Roles' : `${selectedRoles.length} Role${selectedRoles.length > 1 ? 's' : ''} Selected`}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div className="p-2">
                <div onClick={() => setSelectedRoles([])} className="px-2 py-1.5 text-sm cursor-pointer hover:bg-stone-100 rounded">
                  <input type="checkbox" checked={selectedRoles.length === 0} readOnly className="mr-2" />
                  All Roles
                </div>
                {[...new Set(shifts.map(s => s.role_name))].sort().map(roleName => (
                  <div
                    key={roleName}
                    onClick={() => {
                      setSelectedRoles(prev =>
                        prev.includes(roleName) ? prev.filter(r => r !== roleName) : [...prev, roleName]
                      );
                    }}
                    className="px-2 py-1.5 text-sm cursor-pointer hover:bg-stone-100 rounded"
                  >
                    <input type="checkbox" checked={selectedRoles.includes(roleName)} readOnly className="mr-2" />
                    {roleName}
                  </div>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {getWeekDates().map((date, idx) => {
          const dayShifts = shifts.filter(s => {
            const dateMatch = s.date === date;
            const roleMatch = selectedRoles.length === 0 || selectedRoles.includes(s.role_name);
            return dateMatch && roleMatch;
          });
          const dayName = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx];
          const dayTotalHours = dayShifts.reduce((sum, s) => {
            const h = calcShiftHours(s.start_time, s.end_time, s.lunch_minutes || 0);
            return sum + h * (s.assigned_users?.length || 0);
          }, 0);
          const dayAvailableHours = dayShifts.reduce((sum, s) => {
            const h = calcShiftHours(s.start_time, s.end_time, s.lunch_minutes || 0);
            return sum + h * (s.max_capacity || 1);
          }, 0);
          return (
            <div key={date} className="border border-stone-200 rounded-md p-1.5 bg-stone-50 min-h-[80px]">
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-semibold text-xs text-stone-800">{dayName}</span>
                <div className="text-right">
                  <span className="text-[10px] text-stone-500">{new Date(date).getDate()}</span>
                  {dayAvailableHours > 0 && (
                    <div className="text-[10px] font-medium text-emerald-700" data-testid={`day-hours-${idx}`}>
                      {Math.round(dayTotalHours * 10) / 10}/{Math.round(dayAvailableHours * 10) / 10}h
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                {dayShifts.map(shift => (
                  <div
                    key={shift.id}
                    data-testid={`shift-card-${shift.shift_code}`}
                    className={`p-1.5 rounded text-xs cursor-pointer hover:shadow-md transition ${
                      shift.status === 'assigned'
                        ? 'bg-green-100 border border-green-300 text-green-800'
                        : shift.status === 'requested'
                        ? 'bg-yellow-100 border border-yellow-300 text-yellow-800'
                        : 'bg-white border border-stone-300 text-stone-700'
                    }`}
                    onClick={() => {
                      if (!isOwnBranch) return;
                      handleEditShift(shift);
                    }}
                    style={{ cursor: isOwnBranch ? 'pointer' : 'default' }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{shift.shift_code}</div>
                        <div className="text-xs">{shift.start_time}-{shift.end_time}</div>
                        {(() => {
                          const assigned = shift.assigned_users?.length || (shift.assigned_username ? 1 : 0);
                          const cap = shift.max_capacity || 1;
                          const hasRoom = assigned < cap;
                          return (
                            <>
                              <div className={`text-xs mt-1 font-semibold ${assigned >= cap ? 'text-green-700' : 'text-stone-500'}`}>
                                {assigned}/{cap}
                              </div>
                              {shift.assigned_users && shift.assigned_users.length > 0 ? (
                                <div className="text-xs font-medium mt-0.5 space-y-0.5 overflow-hidden max-w-full">
                                  {shift.assigned_users.map(u => {
                                    const unavailReason = unavailMap[u.user_id]?.[shift.date];
                                    return (
                                    <div key={u.user_id} className="flex items-center gap-1 group/name">
                                      {u.assigned_by === 'auto' && <Wand2 className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" title="Auto-scheduled" />}
                                      {unavailReason && <AlertTriangle className="w-2.5 h-2.5 text-amber-500 flex-shrink-0" title={`Unavailable: ${unavailReason}`} />}
                                      <span className={`truncate leading-tight flex-1 ${unavailReason ? 'text-amber-700' : ''}`}>{u.username}</span>
                                      {isOwnBranch && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleUnassignUser(shift.id, u.user_id); }}
                                          className="flex-shrink-0 w-3.5 h-3.5 rounded-full bg-red-200 hover:bg-red-400 text-red-700 hover:text-white flex items-center justify-center opacity-0 group-hover/name:opacity-100 transition-opacity"
                                          title={`Remove ${u.username}`}
                                          data-testid={`remove-user-${u.user_id}`}
                                        >
                                          <X className="w-2.5 h-2.5" />
                                        </button>
                                      )}
                                    </div>
                                    );
                                  })}
                                </div>
                              ) : shift.assigned_username ? (
                                <div className="text-xs font-medium truncate">{shift.assigned_username}</div>
                              ) : null}
                              {hasRoom && isOwnBranch && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openAssignDialog(shift); }}
                                  className="mt-1 w-full flex items-center justify-center gap-0.5 py-0.5 rounded border border-dashed border-stone-300 hover:border-blue-400 hover:bg-blue-50 text-stone-400 hover:text-blue-600 transition-colors"
                                  data-testid={`add-staff-${shift.id}`}
                                  title="Assign staff"
                                >
                                  <Plus className="w-3 h-3" />
                                  <span className="text-[10px]">Assign</span>
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
